import React from "react";
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  styled,
  Divider,
  Grid,
  FormHelperText,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";

const PreviewContainer = styled(Box)(({ theme }) => ({
  position: "relative",
  width: 90,
  height: 90,
  borderRadius: theme.shape.borderRadius,
  overflow: "hidden",
  boxShadow: theme.shadows[1],
  "&:hover .image-actions": {
    opacity: 1,
  },
}));

const PreviewImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
});

const ImageActions = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: 0,
  right: 0,
  width: "100%",
  height: "100%",
  background: "rgba(0,0,0,0.4)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  opacity: 0,
  transition: "opacity 0.3s",
}));

const UploadBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
}));

const UploadInput = styled("input")({
  display: "none",
});

const ImageUploadSection = ({
  primaryPreview,
  secondaryPreviews,
  handlePrimaryImageChange,
  handleSecondaryImagesChange,
  onRemoveSecondaryImage,
  formErrors, // 👈 added formErrors
}) => {
  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Product Images
      </Typography>

      <Stack spacing={3}>
        {/* Primary Image */}
        <Box>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Primary Image
          </Typography>
          <UploadBox>
            <label htmlFor="primary-upload">
              <UploadInput
                id="primary-upload"
                type="file"
                accept="image/*"
                onChange={handlePrimaryImageChange}
              />
              <Tooltip title="Upload Primary Image">
                <IconButton color="primary" component="span">
                  <UploadFileIcon />
                </IconButton>
              </Tooltip>
            </label>

            {primaryPreview && (
              <PreviewContainer>
                <PreviewImage src={primaryPreview} alt="Primary Preview" />
              </PreviewContainer>
            )}
          </UploadBox>

          {/* Primary image error */}
          {formErrors?.primaryImage && (
            <FormHelperText error sx={{ mt: 1 }}>
              {formErrors.primaryImage}
            </FormHelperText>
          )}
        </Box>

        <Divider />

        {/* Secondary Images */}
        <Box>
          <Typography variant="body2" fontWeight="bold" gutterBottom>
            Secondary Images
          </Typography>
          <UploadBox>
            <label htmlFor="secondary-upload">
              <UploadInput
                id="secondary-upload"
                type="file"
                accept="image/*"
                multiple
                onChange={handleSecondaryImagesChange}
              />
              <Tooltip title="Upload Secondary Images">
                <IconButton color="primary" component="span">
                  <UploadFileIcon />
                </IconButton>
              </Tooltip>
            </label>
          </UploadBox>

          <Grid container spacing={1} sx={{ mt: 2 }}>
            {secondaryPreviews.map((preview, index) => (
              <Grid item key={index}>
                <PreviewContainer>
                  <PreviewImage src={preview} alt={`Secondary ${index}`} />
                  <ImageActions className="image-actions">
                    <Tooltip title="Remove">
                      <IconButton
                        size="small"
                        sx={{ color: "white" }}
                        onClick={() => onRemoveSecondaryImage(index)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </ImageActions>
                </PreviewContainer>
              </Grid>
            ))}
          </Grid>
        </Box>
      </Stack>
    </Box>
  );
};

export default ImageUploadSection;
